import { AfterViewInit, Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Validators } from '@angular/forms';
import * as FileSaver from 'file-saver';
import { IvFormComponent } from '../../../shared/components/iv-form/iv-form.component';
import { PersonalizationsService } from '../../../base/services/personalizations/personalizations.service';
import { Environment } from '../../../models/environment';
import { DocumentService, OperationService } from '../../../api';
import { TranslateService } from '../../../core/translation';
import { cValidators } from '../../../models/validator';
import { LoadingService } from '../../../shared/loading/loading.service';
import { Utils } from '../../../shared/utils/utils';
import { ProfileService } from './../../../core/profile/profile.service';
import { MessageService } from './../../../shared/message/message.service';
import { otpFormConfig } from './form/config';
import { FormConfig } from './../../../shared/types/legacy';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-otpsigner',
  templateUrl: './otpsigner.component.html',
  styleUrls: ['./otpsigner.component.scss'],
  providers: [OperationService, DocumentService, LoadingService],
})
export class OtpSignerComponent implements OnInit, OnDestroy, AfterViewInit {
  /**
   * IvForm component from platform.
   */
  @ViewChild('ivform') ivform: IvFormComponent;

  /**
   * Event triggered when finish the signature.
   */
  @Output() submit = new EventEmitter<any>();

  /**
   * Event triggered when there's an error.
   */
  @Output() onError = new EventEmitter();

  /**
   * Object containing the form distribution.
   */
  otpFormConfig: FormConfig = null;

  /**
   * Boolean that controls if there's a signature.
   */
  signature = false;

  /**
   * Object containing error labels.
   */
  errorMessages: object = {
    required: this._translate.instant('form_error_required'),
    pattern: this._translate.instant('incorrect_value'),
  };

  token: string;

  /**
   * String that contains operation id.
   */
  operationId: string = null;

  /**
   * Get otp code
   */
  forwardOTP: number;

  /**
   * Get user dni
   */
  dniUser: string;

  /**
   * Get user name
   */
  nameUser: string;

  /**
   * One min disabled sended otp button
   */
  forwardingOTPButton: boolean = false;
  /**
   * Object containing data of the operation.
   */
  data: any = null;
  idCircuit: string;
  valueTemplate: string;
  sendedOTP = false;
  documentId: string;
  customEvents: boolean;

  environment: Environment;

  destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private operationService: OperationService,
    private _translate: TranslateService,
    private _profile: ProfileService,
    private _message: MessageService,
    public documentService: DocumentService,
    public _loading: LoadingService,
    private _personalization: PersonalizationsService
  ) {
    this.environment = this._personalization.sendEnv;
  }

  ngOnInit() {
    this.customEvents = this.environment?.features.customEvents || false;
    if (this._profile.getToken()) {
      this.token = this._profile.getToken();
    } else {
      this.token = this._profile.getPublicToken();
    }
    for (const key in otpFormConfig) {
      if (otpFormConfig[key]._label) {
        otpFormConfig[key].label = this._translate.instant(
          otpFormConfig[key]._label
        );
      }
    }
    this.otpFormConfig = Utils.clone(otpFormConfig);
    const data = {
      name: this.data?.nameUser,
      nif: this.data?.dniUser,
    };
    if (data.name.includes('@')) {
      this.otpFormConfig.name.label = 'Email';
    }
    this.otpFormConfig.name.value = data.name;
    this.otpFormConfig.nif.value = data.nif;
    this.otpFormConfig.otp.label = this._translate.instant('finish_label_otp');
    this.otpFormConfig.otp._label = 'finish_label_otp';

    if (!data.name) {
      this.otpFormConfig.name.disabled = false;
    }
    if (!data.nif) {
      this.otpFormConfig.nif.disabled = false;
    }
    if (this.valueTemplate == 'CONTRAC' || this.valueTemplate == 'PRECONTRAC') {
      this.otpFormConfig.name.hidden = true;
      this.otpFormConfig.nif.hidden = true;
      this.otpFormConfig.otp.label = '';
      this.otpFormConfig.otp._label = '';
    }
    navigator.credentials
      .get({
        otp: { transport: ['sms'] },
      })
      .then((otp) => {
        this.ivform.Form.patchValue({ otp: otp.code });
      })
      .catch((err) => {});
  }

  ngAfterViewInit() {
    const otp = this.ivform.Form?.controls.otp;
    otp.setValidators(
      Validators.compose([
        Validators.pattern('^[0-9]*$'),
        cValidators.required,
        cValidators.hasLength(8),
      ])
    );
    otp.updateValueAndValidity();
    const nif = this.ivform.Form?.controls.nif;
    nif.setValidators(
      Validators.compose([
        Validators.pattern('^([0-9]{8,8}|[XYZ][0-9]{7})[A-Z]$'),
        cValidators.required,
      ])
    );
    nif.updateValueAndValidity();
    if (this.environment?.features.disableValidators?.nif) {
      this.otpFormConfig.nif.validators = ['required'];
      const nif = this.ivform.Form?.controls.nif;
      nif.setValidators(
      Validators.compose([cValidators.required]));
    }
  }

  ngOnDestroy() {
    this.otpFormConfig.name.disabled = true;
    this.otpFormConfig.nif.disabled = true;
    this.otpFormConfig.name.value = null;
    this.otpFormConfig.nif.value = null;
  }

  /**
   * Function that control if there's a signature.
   * @param {boolean} value Boolean that contains if there's a signature.
   */
  setSignature(value: boolean) {
    this.signature = value;
  }

  /**
   * Function that sets data of the operation.
   * @param {any} data Object that contains data .
   */
  setData(data: any) {
    this.operationId = data.id;
    this.data = data.formData;
    this.valueTemplate = this.data.codeTemplate;
    this.idCircuit = this.data.idCircuit;
    this.documentId = this.data.documentId;
    this.forwardOTP = this.data.forwardOTP;
    this.dniUser = this.data.dniUser;
    this.nameUser = this.data.nameUser;
  }

  /**
   * Function that controls signing pdf.
   * @param {Function} callback Function triggered when finish the server petition.
   */
  sign(callback?: Function) {
    if (!this.ivform.Form.valid) {
      this.emitError(null);
      this.ivform.Form.markAllAsTouched();
      this.ivform.Form.updateValueAndValidity();

      return;
    }
    const metadata = [];
    const keys = Object.keys(this.ivform.Form.getRawValue());
    for (const key of keys) {
      metadata.push({ key: key, value: this.ivform.Form.get(key).value });
    }

    if (callback) {
      callback(metadata);
    }
  }

  /**
   * Function that send in an sms a code to the user phone.
   * @param {Function} callback Function triggered when finish the server petition.
   */
  sendOTP(callback?: Function) {
    const token = this._profile.getToken() || '1';
    this.operationService.operationGetSendOTP(
      this.operationId,
      this.environment.appid,
      token
    ).then(
      (response) => {
        if (this.customEvents) {
          window.top.postMessage({ type: 'onFinish', result: 'OK' }, window.parent.origin);
        }
        this.sendedOTP = true;
        callback(true);
      },
      (error) => {
        this.sendedOTP = false;
        if (this.customEvents) {
          window.top.postMessage({ type: 'onFinish', result: 'KO' }, window.parent.origin);
        }
        this._message.showError(error);
        callback(false);
      }
    );
  }

  /**
   * Function to resend otp code.
   */
  forwardingOTP() {
    this.forwardingOTPButton = true;
    const token = this._profile.getToken() || '1';
    this.operationService.operationGetForwardingOTP(
      this.operationId,
      this.environment.appid,
      token
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {});
    setTimeout(() => {
      this.forwardingOTPButton = false;
    }, 60000);
  }

  /**
   * Function triggered wuen there's an error.
   * @param {any} error Object that contains the form configuration.
   */
  emitError(error: any) {
    this.onError.emit(error);
  }

  getAllDocs() {
    this._loading.set(true);
    this.documentService
      .documentGetDownloadDocumentCircuit(
        this.idCircuit,
        this.documentId,
        this.token,
        this.environment?.appid,
        this.operationId,
        false,
        'response',
      )
      .then((response) => {
        const document = response.body;
        const reader = new FileReader();
        this._loading.set(false);
        const file = new Blob([document], { type: 'application/pdf' });
        FileSaver.saveAs(file, this.data.fileName);
      });
    this._loading.set(false);
  }

  verifyOTP() {
    if (this.ivform.Form?.valid) {
      return true;
    }

    return false;
  }
}
